// src/components/DocumentUploader.js
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { FileText, Plus, X, ArrowRight, RefreshCw, AlertCircle, CheckCircle, Upload } from 'lucide-react';
import { API_URL } from '../utils/apiConfig';
import { bartFieldDetection } from '../api/documents';
import { useAnalytics } from '../hooks/useAnalytics';

const DocumentUploader = ({
  user,
  onUploadSuccess,
  onCancel,
  showSignatureFieldsCallback,
  embedded = false,
  onUploadOption = null,
  useTrainingEndpoint = false
}) => {
  const { trackDocument, trackEvent } = useAnalytics();
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentNameInput, setDocumentNameInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');
  // const [message, setMessage] = useState(''); // Moved to Review step
  const [uploadStatus, setUploadStatus] = useState('');
  const [conversionInfo, setConversionInfo] = useState(null);

  // Upload option state - default to regular upload
  const [selectedUploadOption, setSelectedUploadOption] = useState('regular'); // 'regular' or 'autoplace'

  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  // Validate and process file
  const processFile = useCallback((file) => {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      alert(`File type ${file.type} is not supported. Please upload PDF, DOC, DOCX, or TXT files.`);
      return false;
    }
    
    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      alert('File size must be less than 50MB.');
      return false;
    }
    
    setSelectedFile(file);
    
    // Auto-populate the document name with the file name (minus extension)
    const fileName = file.name.split('.').slice(0, -1).join('.');
    setDocumentNameInput(fileName || 'Untitled Document');
    
    // Show conversion and watermarking info
    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword') {
      setConversionInfo('This document will be converted to PDF and certified with SignPact fingerprinting.');
    } else if (file.type === 'application/pdf') {
      setConversionInfo('This document will be certified with SignPact fingerprinting for authenticity.');
    } else {
      setConversionInfo(null);
    }
    
    return true;
  }, []);

  const handleFileSelection = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Track click upload method
      trackDocument('document_upload_start', {
        fileType: file.type,
        fileSize: file.size,
        additionalParams: {
          file_name_length: file.name.length,
          has_extension: file.name.includes('.'),
          upload_method: 'click'
        }
      });
      processFile(file);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter <= 1) {
      setIsDragOver(false);
    }
  }, [dragCounter]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragOver(false);
    setDragCounter(0);
    
    if (isSubmitting) return; // Don't allow drop during upload
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Track drag and drop upload method
      trackDocument('document_upload_start', {
        fileType: file.type,
        fileSize: file.size,
        additionalParams: {
          file_name_length: file.name.length,
          has_extension: file.name.includes('.'),
          upload_method: 'drag_drop'
        }
      });
      processFile(file);
    }
  }, [isSubmitting, processFile, trackDocument]);

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }
    
    setIsSubmitting(true);
    setUploadStatus('Uploading document...');
    
    try {
      // Show conversion and watermarking status
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          selectedFile.type === 'application/msword') {
        setUploadStatus('Converting to PDF and applying SignPact certification...');
      } else if (selectedFile.type === 'application/pdf') {
        setUploadStatus('Applying SignPact certification...');
      }
      
      // Create FormData for the upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (!useTrainingEndpoint) {
        formData.append('name', documentNameInput);
        // Explicitly control server-side auto-processing based on current selection
        formData.append('autoProcess', selectedUploadOption === 'autoplace' ? 'true' : 'false');
      } else {
        // Map upload option to training mode
        const mode = selectedUploadOption === 'autoplace' ? 'auto' : 'manual';
        formData.append('mode', mode);
        formData.append('metadata', JSON.stringify({ source: 'training-uploader' }));
      }
      
      if (expirationDate) {
        formData.append('expirationDate', expirationDate);
      }
      
      // Message is now handled in the Review step
      // if (message) {
      //   formData.append('message', message);
      // }
      
      // Upload the document (documents or training endpoint)
      const uploadUrl = useTrainingEndpoint 
        ? `${API_URL}/training/upload`
        : `${API_URL}/documents/upload`;
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      console.log('Document uploaded:', response.data);
      
      // Track successful upload
      if (!useTrainingEndpoint) {
        trackDocument('document_upload_success', {
          id: response.data.id,
          fileType: response.data.fileType,
          fileSize: response.data.fileSize,
          status: response.data.status,
          additionalParams: {
            converted: response.data.converted || false,
            watermarked: response.data.watermarked || false,
            preview_scheduled: response.data.previewScheduled || false,
            has_expiration: !!expirationDate,
            has_message: false
          }
        });
      } else {
        trackDocument('training_upload_success', {
          documentId: response.data.documentId,
          trainingMode: response.data.trainingMode,
          autoProcessed: !!response.data?.results?.autoProcessed
        });
      }
      
      // Show success message with appropriate details
      if (!useTrainingEndpoint) {
        if (response.data.converted && response.data.watermarked) {
          setUploadStatus('Document converted to PDF and certified successfully!');
        } else if (response.data.watermarked) {
          setUploadStatus('Document certified with SignPact fingerprinting!');
        } else if (response.data.converted) {
          setUploadStatus('Document converted to PDF successfully!');
        } else {
          setUploadStatus('Document uploaded successfully!');
        }
      } else {
        setUploadStatus('Training upload completed');
      }

      const uploadedDoc = response.data;

      if (!useTrainingEndpoint && selectedUploadOption === 'autoplace') {
        try {
          setUploadStatus('Analyzing document and auto-assigning fields...');
          // Run BART field detection/creation
          const bartResponse = await bartFieldDetection(uploadedDoc.id, {
            minConfidence: 0.4,
            maxFieldsPerPage: 8,
            includeContext: true,
            prioritizeSignatures: true,
            maxTotalFields: 25,
            autoCreateFields: true
          });

          const createdFields = bartResponse?.data?.creation?.createdFields || 0;
          const totalRecommendations = bartResponse?.data?.analysis?.recommendations?.length || 0;
          console.log('🧠 Auto-assign completed. Created fields:', createdFields, 'of', totalRecommendations);

          // Warm the form-fields endpoint to ensure UI sees fresh data
          try {
            const ffRes = await axios.get(`${API_URL}/documents/${uploadedDoc.id}/form-fields`, {
              headers: { 'Authorization': `Bearer ${user.token}` }
            });
            console.log('🧠 Warmed form-fields endpoint. Count:', Array.isArray(ffRes.data?.formFields) ? ffRes.data.formFields.length : (Array.isArray(ffRes.data) ? ffRes.data.length : 'n/a'));
          } catch (ffErr) {
            console.warn('⚠️ Failed to warm form-fields endpoint (non-fatal):', ffErr);
          }

          // Warm signatures/requests endpoint as well
          try {
            const sigRes = await axios.get(`${API_URL}/signatures/document/${uploadedDoc.id}`, {
              headers: { 'Authorization': `Bearer ${user.token}` }
            });
            const reqCount = Array.isArray(sigRes.data?.requests) ? sigRes.data.requests.length : 0;
            console.log('🧠 Warmed signatures endpoint. Requests:', reqCount);
          } catch (sigErr) {
            console.warn('⚠️ Failed to warm signatures endpoint (non-fatal):', sigErr);
          }

          // Poll for availability so fields show on first load
          const pollUntilFieldsAvailable = async (maxAttempts = 20, delayMs = 300) => {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              try {
                const [ffRes, sigRes] = await Promise.all([
                  axios.get(`${API_URL}/documents/${uploadedDoc.id}/form-fields`, { headers: { 'Authorization': `Bearer ${user.token}` } }),
                  axios.get(`${API_URL}/signatures/document/${uploadedDoc.id}`, { headers: { 'Authorization': `Bearer ${user.token}` } })
                ]);

                const formCount = Array.isArray(ffRes.data?.formFields) ? ffRes.data.formFields.length : 0;
                const reqCount = Array.isArray(sigRes.data?.requests) ? sigRes.data.requests.length : 0;
                console.log(`🔎 Poll attempt ${attempt}: formFields=${formCount}, signatureRequests=${reqCount}`);

                if (formCount > 0 || reqCount > 0) return true;
              } catch (e) {
                // ignore and retry
              }
              await new Promise(r => setTimeout(r, delayMs));
            }
            return false;
          };

          const fieldsReady = await pollUntilFieldsAvailable();
          if (!fieldsReady && (createdFields || 0) > 0) {
            // If backend reported created fields but polling did not catch up, wait a bit more
            await new Promise(r => setTimeout(r, 1000));
          }

          setUploadStatus(createdFields > 0
            ? `Auto-assigned ${createdFields} fields successfully!`
            : 'Analysis completed. No confident fields created.');

          // Proceed to next step so the placement UI loads with fields present
          if (onUploadSuccess) {
            onUploadSuccess(uploadedDoc);
          }
        } catch (autoErr) {
          console.error('❌ Auto-assign failed, proceeding without it:', autoErr);
          setUploadStatus('Document uploaded. Auto-assign failed, proceeding to manual placement.');
          if (onUploadSuccess) {
            onUploadSuccess(uploadedDoc);
          }
        }
      } else if (!useTrainingEndpoint) {
        // Regular flow
        setTimeout(() => {
          if (onUploadSuccess) {
            onUploadSuccess(uploadedDoc);
          }
        }, 1000);
      } else {
        // Training flow: return training payload directly
        if (onUploadSuccess) {
          onUploadSuccess(uploadedDoc);
        }
      }
      
    } catch (error) {
      console.error('Error uploading document:', error);
      
      // Track upload failure
      trackDocument('document_upload_failure', {
        fileType: selectedFile?.type,
        fileSize: selectedFile?.size,
        additionalParams: {
          error_type: error.response?.data?.error?.includes('convert') ? 'conversion_error' : 'upload_error',
          error_message: error.response?.data?.error || error.message
        }
      });
      
      if (error.response?.data?.error?.includes('convert')) {
        alert('Failed to convert document. Please try uploading a PDF file instead.');
      } else {
        alert('Failed to upload document. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {!selectedFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-16 text-center transition-all duration-200 ${
            isDragOver 
              ? 'border-blue-500 bg-blue-50 shadow-lg scale-105' 
              : 'border-gray-300 hover:bg-gray-50 hover:shadow-sm hover:-translate-y-0.5'
          } ${isSubmitting ? 'pointer-events-none opacity-50' : ''}`}
          role="region"
          aria-label="Select document to upload or drag and drop"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isDragOver ? (
            <>
              <Upload size={64} className="mx-auto text-blue-500 mb-6 animate-bounce" aria-hidden="true" />
              <p className="text-blue-600 font-medium text-lg mb-2">Drop your document here</p>
              <p className="text-sm text-blue-500">Release to upload</p>
            </>
          ) : (
            <>
              <FileText size={64} className="mx-auto text-gray-300 mb-6" aria-hidden="true" />
              <p className="text-gray-600 mb-3 text-lg">Drag and drop your document here</p>
              <p className="text-gray-500 text-sm mb-6">or click to browse files</p>
              <input 
                type="file" 
                className="hidden" 
                id="document-upload" 
                onChange={handleFileSelection}
                accept=".pdf,.doc,.docx,.txt"
                disabled={isSubmitting}
              />
              <label 
                htmlFor="document-upload"
                className="relative inline-flex items-center justify-center cursor-pointer select-none rounded-xl px-6 py-3 text-white font-medium shadow-sm transition-all duration-300 group"
                style={{
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                }}
                aria-label="Choose file to upload"
              >
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)',
                    animation: 'shimmer 2s infinite',
                    borderRadius: '12px'
                  }}
                />
                <Plus size={18} className="mr-2 relative z-10" aria-hidden="true" />
                <span className="relative z-10">Choose File</span>
              </label>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div
            className="rounded-2xl border p-6 transition-colors"
            style={{ borderColor: 'rgba(148,163,184,0.2)', backgroundColor: 'white' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="mr-4 p-3 rounded-lg" style={{ background: 'rgba(30, 64, 175, 0.08)' }}>
                  <FileText className="text-blue-600" size={24} aria-hidden="true" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 text-lg">{selectedFile.name}</div>
                  <div className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
              </div>
              <button 
                onClick={() => {
                  setSelectedFile(null);
                  setConversionInfo(null);
                  setDocumentNameInput('');
                }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
                aria-label="Remove selected file"
              >
                <X size={20} className="text-gray-500" aria-hidden="true" />
              </button>
            </div>

            {conversionInfo && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start">
                <AlertCircle size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <p className="text-sm text-blue-800">{conversionInfo}</p>
              </div>
            )}

          </div>


          {uploadStatus && (
            <div
              className="rounded-xl border p-4 flex items-start gap-3"
              style={{ borderColor: 'rgba(148,163,184,0.2)', backgroundColor: 'white' }}
              role="status"
              aria-live="polite"
            >
              <div className="mt-0.5">
                {isSubmitting ? (
                  <RefreshCw size={20} className="text-gray-500 animate-spin" aria-hidden="true" />
                ) : (
                  <CheckCircle size={20} className="text-green-600" aria-hidden="true" />
                )}
              </div>
              <p className="text-sm text-gray-700">{uploadStatus}</p>
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                setSelectedUploadOption('regular');
                setTimeout(() => handleUpload(), 100);
              }}
              className="relative inline-flex items-center px-6 py-3 rounded-xl text-white font-medium transition-all duration-300 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                opacity: (!selectedFile || isSubmitting) ? 0.6 : 1
              }}
              disabled={!selectedFile || isSubmitting}
              aria-label="Upload"
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)',
                  animation: 'shimmer 2s infinite',
                  borderRadius: '12px'
                }}
              />
              {isSubmitting && selectedUploadOption === 'regular' ? (
                <>
                  <RefreshCw size={18} className="mr-2 animate-spin" aria-hidden="true" />
                  Uploading...
                </>
              ) : (
                <>
                  Upload
                  <ArrowRight size={18} className="ml-2" aria-hidden="true" />
                </>
              )}
            </button>
            {/*
              Temporarily disabled: Upload & Auto Assign Fields button.
              Keeping logic intact for future re-enable.
            <button
              onClick={() => {
                setSelectedUploadOption('autoplace');
                setTimeout(() => handleUpload(), 100);
              }}
              className="relative inline-flex items-center px-6 py-3 rounded-xl text-white font-medium transition-all duration-300 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                opacity: (!selectedFile || isSubmitting) ? 0.6 : 1
              }}
              disabled={!selectedFile || isSubmitting}
              aria-label="Upload and auto assign fields"
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)',
                  animation: 'shimmer 2s infinite',
                  borderRadius: '12px'
                }}
              />
              {isSubmitting && selectedUploadOption === 'autoplace' ? (
                <>
                  <RefreshCw size={18} className="mr-2 animate-spin" aria-hidden="true" />
                  Auto-assigning fields...
                </>
              ) : (
                <>
                  Upload & Auto Assign Fields
                  <ArrowRight size={18} className="ml-2" aria-hidden="true" />
                </>
              )}
            </button>
            */}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;